import { ExistingPolicy, PastApplication } from '../../common/dataTypes';
import { FetchService } from '../../common/fetch.service';
import {
  filterToGuidelineRuleSets,
  stripWorkflowSuffix,
} from '../../common/helpers/rule-results/filter';
import { HttpService } from '../../common/http.service';
import { IRuleResults } from '../detail/interfaces/IRuleResults';
import { ITask } from '../tasks/interfaces/ITask';
import { Evidence } from '../tasks/types/evidence';
import {
  getCarrierPolicyNumbersToPolicyIdsEndPoint,
  getExistingPoliciesEndPoint,
  getMibCodebackDataByPolicyId,
  getPolicyDecisionEndPoint,
  getPolicyEndPoint,
  getPolicyEvidenceEndPoint,
  getPolicyEvidenceListEndPoint,
  getPolicyRuleRawResultEndPoint,
  getPolicyRuleResultEndPoint,
  getTaxonomyOptionsEndPoint,
} from './api.endPoints';
import { EvidenceInfo } from './dtos/evidenceInfo.dto';
import { PolicyData } from './dtos/policyData.dto';

export async function getPolicy(policyId: string): Promise<PolicyData | null> {
  const data = await HttpService.getInstance().get(getPolicyEndPoint(policyId));
  if (data && !data.error && Object.keys(data.parsedBody).length !== 0) {
    return data.parsedBody as PolicyData;
  }
  return null;
}

export async function getPolicyIdByCarrierPolicyNumber(
  carrierPolicyNumber: string,
): Promise<number | null> {
  const data = await HttpService.getInstance().post(
    getCarrierPolicyNumbersToPolicyIdsEndPoint(),
    [carrierPolicyNumber],
  );
  if (data && !data.error) {
    return data.parsedBody?.[carrierPolicyNumber];
  }
  return null;
}

export async function fetchPolicyRuleResult(
  policyId: string,
  decisionId: string,
): Promise<IRuleResults | null> {
  const data = await HttpService.getInstance().get(
    getPolicyRuleResultEndPoint(policyId, decisionId),
  );
  if (data && !data.error) {
    return data.parsedBody as IRuleResults;
  }
  return null;
}

export async function fetchPolicyRuleRawResult(
  policyId: string,
  decisionId: number,
) {
  const data = await HttpService.getInstance().get(
    getPolicyRuleRawResultEndPoint(policyId, decisionId),
  );
  if (data && !data.error) return data.parsedBody;
  return null;
}

export async function fetchEvidenceData(
  product: string,
  carrier: string,
  policyId: string,
  evidenceId: string,
  type: string,
): Promise<Evidence | null> {
  const data = await HttpService.getInstance().get(
    getPolicyEvidenceEndPoint(product, carrier, policyId, evidenceId, type),
  );
  if (data && !data.error) {
    return data.parsedBody as Evidence;
  }
  return null;
}

export const PPWB_RULE_RESULT_DECISION_LABELS = {
  Engine: 'Engine',
  SoftDecline: 'Soft Decision',
} as const;

export type PPWBRuleResultDecisionLabel =
  (typeof PPWB_RULE_RESULT_DECISION_LABELS)[keyof typeof PPWB_RULE_RESULT_DECISION_LABELS];

export const PPWB_RULE_RESULT_DECISION_TYPE_ORDER: PPWBRuleResultDecisionLabel[] =
  [
    PPWB_RULE_RESULT_DECISION_LABELS.Engine,
    PPWB_RULE_RESULT_DECISION_LABELS.SoftDecline,
  ];

export function isPPWBRuleResultDecisionLabel(
  label: string,
): label is PPWBRuleResultDecisionLabel {
  return (
    label === PPWB_RULE_RESULT_DECISION_LABELS.Engine ||
    label === PPWB_RULE_RESULT_DECISION_LABELS.SoftDecline
  );
}

export function getPPWBRuleResultDecisionLabel(
  decisionType: string,
): PPWBRuleResultDecisionLabel | null {
  if (decisionType === 'SoftDecline') {
    return PPWB_RULE_RESULT_DECISION_LABELS.SoftDecline;
  }
  if (decisionType === 'Engine') {
    return PPWB_RULE_RESULT_DECISION_LABELS.Engine;
  }
  return null;
}

export function getAvailablePPWBRuleResultDecisionLabels(
  decisions: { decisionType: string }[],
): PPWBRuleResultDecisionLabel[] {
  const labels = new Set(
    decisions
      .map(decision => getPPWBRuleResultDecisionLabel(decision.decisionType))
      .filter((label): label is PPWBRuleResultDecisionLabel => label !== null),
  );

  return PPWB_RULE_RESULT_DECISION_TYPE_ORDER.filter(label => labels.has(label));
}

export function getPPWBDecisionTypeFromLabel(
  label: PPWBRuleResultDecisionLabel,
): string {
  if (label === PPWB_RULE_RESULT_DECISION_LABELS.SoftDecline) {
    return 'SoftDecline';
  }
  return 'Engine';
}

export function getLatestPPWBDecisionByType(
  decisions: { decisionType: string; createdAt?: string; id?: number }[],
  decisionType: string,
): { [key: string]: any } | undefined {
  return decisions
    .filter(decision => decision.decisionType === decisionType)
    .reduce<{ [key: string]: any } | undefined>((latest, current) => {
      if (!latest) {
        return current;
      }

      const latestTime = latest.createdAt
        ? new Date(latest.createdAt).getTime()
        : 0;
      const currentTime = current.createdAt
        ? new Date(current.createdAt).getTime()
        : 0;

      if (currentTime > latestTime) {
        return current;
      }
      if (
        currentTime === latestTime &&
        (current.id ?? 0) > (latest.id ?? 0)
      ) {
        return current;
      }
      return latest;
    }, undefined);
}

const validDecisions = ['SoftDecline', 'Engine'];
export async function fetchDecision(
  product: string,
  carrier: string,
  policyId: string,
): Promise<any[] | null> {
  const data = await HttpService.getInstance().get(
    getPolicyDecisionEndPoint(product, carrier, policyId),
  );
  if (data && !data.error) {
    return (data.parsedBody as any[]).filter(item =>
      validDecisions.includes(item.decisionType),
    );
  }
  return null;
}

export async function fetchEvidenceList(
  product: string,
  carrier: string,
  policyId: string,
  all?: boolean,
): Promise<EvidenceInfo[] | null> {
  const data = await HttpService.getInstance().get(
    getPolicyEvidenceListEndPoint(product, carrier, policyId, all),
  );
  if (data && !data.error) {
    return data.parsedBody as EvidenceInfo[];
  }
  return null;
}

export async function fetchMIBCodeBackData(
  policyId: string,
): Promise<ITask | null> {
  const data = await HttpService.getInstance().get(
    getMibCodebackDataByPolicyId(policyId),
  );
  if (data && !data.error) {
    return data.parsedBody as ITask;
  }
  return null;
}

// TODO: support subCategory after policy admin and maestro support
export async function getExistingPolicies(policyId: string): Promise<{
  activatedPolicies: ExistingPolicy[];
  maestroPolicies: PastApplication[];
}> {
  return await FetchService.getInstance().get(
    getExistingPoliciesEndPoint(policyId),
  );
}

/**
 * Fetch taxonomy options from MDM entities
 */
export async function fetchTaxonomyOptions(): Promise<string[]> {
  const data = await HttpService.getInstance().get(
    getTaxonomyOptionsEndPoint(),
  );
  if (data && !data.error && Array.isArray(data.parsedBody)) {
    return data.parsedBody as string[];
  }
  return [];
}

/**
 * Get ruleset name options from the most recent auto-decision rule results
 * Returns only guideline rulesets (filters out conditional rulesets and Default Decision)
 * This matches the filtering logic in RuleResults.tsx
 */
export async function getRulesetOptionsFromDecision(
  product: string,
  carrier: string,
  policyId: string,
): Promise<string[]> {
  try {
    // 1. Fetch decisions (already filtered to SoftDecline and Engine)
    const decisions = await fetchDecision(product, carrier, policyId);
    if (!decisions || decisions.length === 0) {
      return [];
    }

    // 2. Get the latest Engine decision (same selection as Rule Results default)
    const decision = getLatestPPWBDecisionByType(decisions, 'Engine');
    if (!decision?.id) {
      return [];
    }

    // 3. Fetch rule results for this decision
    const ruleResults = await fetchPolicyRuleResult(
      policyId,
      decision.id.toString(),
    );
    if (!ruleResults?.ruleSets) {
      return [];
    }

    // 4. Filter to get only guideline rulesets (same logic as RuleResults.tsx)
    const guidelineRulesets = filterToGuidelineRuleSets(ruleResults.ruleSets);

    // 5. Extract unique ruleset names (strip "(workflow)" suffix)
    const rulesetNames = guidelineRulesets
      .map(rs => stripWorkflowSuffix(rs.ruleSetName))
      .filter((name): name is string => Boolean(name));

    // Remove duplicates and sort
    return [...new Set(rulesetNames)].sort();
  } catch (error) {
    console.error('Error fetching ruleset options:', error);
    return [];
  }
}
